import "./BeyondWordsPlaylistOverride.css";

import useOKTAUserStore from "@app/store/useOKTAUserStore";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import { IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { useBeyondWordsPlaylistPlayer } from "@hooks/useBeyondWords/useBeyondWordsPlaylistPlayer";
import useBeyondWordsPlaylistPlayerSticky from "@hooks/useBeyondWords/useBeyondWordsPlaylistPlayerSticky";
import { useIsPlayerReady } from "@hooks/useBeyondWords/useIsPlayerReady";
import { OKTAUserServiceFlagEnum } from "@hooks/useUserInfo";
import { useMemo } from "react";

import { canAccessPlaylistItem } from "./helper/canAccessPlaylistItem";
import TTSLookingBanner from "./TTSLookingBanner";

export type PlaylistItem = {
  id: string;
  title: string;
  contentAccess: LabelCardIconVariety;
  isGenG: boolean;
};

export type BeyondWordsPlaylistProps = {
  data: PlaylistItem[];
};

export const VISIBLE_CLASS = "tts-widget-visible";
export const PLAYLIST_STICKY_ID = "tts-sticky-playlist-widget";

export default function BeyondWordsPlaylist({
  data,
}: BeyondWordsPlaylistProps) {
  const userType = useOKTAUserStore((state) => state.userInfo?.usertype);
  const svcFlag = useOKTAUserStore((state) => state.userInfo?.serviceFlags);

  const isGenGUser = !!svcFlag?.includes(OKTAUserServiceFlagEnum.BH_GENG);

  const playlistItems = useMemo(
    () =>
      data
        .filter((item) => canAccessPlaylistItem(item, userType, isGenGUser))
        .map(({ id, title }) => ({
          sourceId: id,
          title,
        })),
    [data, userType, isGenGUser],
  );

  const { playerRef, isPlaying } = useBeyondWordsPlaylistPlayer({
    playlistItems,
  });
  const playerReady = useIsPlayerReady(playerRef);
  const { isStickyVisible } = useBeyondWordsPlaylistPlayerSticky(
    playerRef,
    isPlaying,
  );

  if (IS_PLAYWRIGHT_TEST === "true") return null;

  return (
    <>
      <div className="relative mb-6">
        <div className="z-10 flex flex-col gap-1 items-start w-full">
          <div
            className="w-full"
            ref={playerRef}
            id="tts-playlist-inline-widget"
          />
        </div>
        {playerReady && !isPlaying && (
          <div className="absolute z-0 top-6 left-3 md:-top-4 md:right-7 md:left-auto w-[92%] md:w-[540px] lg:w-[670px]">
            <TTSLookingBanner />
          </div>
        )}
      </div>

      {/* Always present, sticky in normal flow — same as article pattern */}
      <div
        className="tts-sticky-widget-container flex w-full fixed z-30 top-[var(--headerHeight,64px)] right-0 pt-[3px] justify-center transition-opacity duration-300"
        style={{
          opacity: isStickyVisible ? 1 : 0,
          pointerEvents: isStickyVisible ? "auto" : "none",
        }}
      >
        <div
          id={PLAYLIST_STICKY_ID}
          className="w-full md:w-[500px] px-[10px]"
        />
      </div>
    </>
  );
}
